import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { TelemetrySignal } from './TelemetrySignal';
import { TelemetryStatusBand } from './TelemetryStatusBand';
import { trackAppStoreClick } from '@/lib/posthog';

const APP_STORE_URL = 'https://apps.apple.com/us/app/athlete-space/id6758096470';

export const TelemetryHero = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoFailed, setVideoFailed] = useState(false);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = 0.8;
    }
  }, []);

  return (
    <section className="relative min-h-[100svh] flex flex-col">
      {/* Subtle grid background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Grid pattern */}
        <div 
          className="absolute inset-0 opacity-[0.027]"
          style={{
            backgroundImage: `
              linear-gradient(to right, hsl(215, 20%, 30%) 1px, transparent 1px),
              linear-gradient(to bottom, hsl(215, 20%, 30%) 1px, transparent 1px)
            `,
            backgroundSize: '80px 80px',
          }}
        />
        
        {/* Video background with fallback */}
        {!videoFailed ? (
          <video
            ref={videoRef}
            autoPlay
            loop
            muted
            playsInline
            preload="metadata"
            className="absolute inset-0 w-full h-full object-cover opacity-[0.30]"
            style={{ filter: 'saturate(0.75) contrast(1.20)' }}
            onError={() => setVideoFailed(true)}
            onStalled={() => {
              setTimeout(() => {
                const video = videoRef.current;
                if (video && video.readyState < 2) {
                  setVideoFailed(true);
                }
              }, 5000);
            }}
            {...({ 'webkit-playsinline': 'true' } as React.VideoHTMLAttributes<HTMLVideoElement> & { 'webkit-playsinline'?: string })}
          >
            <source src="/Cycling_male.mp4" type="video/mp4" />
          </video>
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 opacity-[0.30]" />
        )}
        
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950/35 via-slate-950/55 to-slate-950" />
      </div>

      {/* Status band at top */}
      <div 
        className="relative z-10"
        style={{ paddingTop: 'calc(4rem + env(safe-area-inset-top, 0px) + 0.5rem)' }}
      >
        <TelemetryStatusBand />
      </div>

      {/* Main hero content */}
      <div className="relative z-10 flex-1 flex flex-col justify-center container mx-auto px-6 py-16">
        <motion.div
          className="mb-12"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2, delay: 0.3 }}
        >
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-mono font-light text-slate-200 mb-6 leading-[1.15] tracking-tight">
            Training, driven by Data and Intelligence.
          </h1>
          
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6 lg:gap-12">
            <h2 className="text-lg md:text-xl lg:text-2xl text-slate-400 font-light leading-relaxed max-w-3xl">
              Your training data becomes a signal.
              <br />
              The signal informs a decision.
              <br />
              The decision drives adaptation.
            </h2>
            
            <Link 
              to="/signup"
              className="flex-shrink-0 px-8 py-3 text-xs font-mono tracking-[0.1em] uppercase bg-slate-200 text-slate-950 hover:bg-white transition-colors"
            >
              Get Started
            </Link>
          </div>
          
          <p className="text-xs font-mono tracking-[0.15em] text-slate-500 uppercase mt-6">
            Closed-loop performance system
          </p>

          <div className="mt-8 flex flex-col items-start gap-2">
            <span className="text-[10px] font-mono tracking-[0.15em] text-slate-500 uppercase">
              Available now on iPhone
            </span>
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackAppStoreClick('hero')}
            >
              <img
                src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
                alt="Download on the App Store"
                className="h-10"
              />
            </a>
          </div>
        </motion.div>

        <div className="relative">
          <TelemetrySignal className="w-full max-w-5xl" animate />
          
          <motion.div 
            className="absolute -bottom-8 left-0 flex gap-8 text-[10px] font-mono tracking-wider text-slate-600"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 3.5, duration: 0.8 }}
          >
            <span>30d</span>
            <span className="text-slate-500">|</span>
            <span>TRAINING LOAD SIGNAL</span>
          </motion.div>
        </div>

        <motion.div 
          className="mt-16 sm:mt-24 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 4, duration: 0.8 }}
        >
          <Link 
            to="/login"
            className="text-sm font-mono tracking-[0.1em] text-slate-400 hover:text-slate-200 transition-colors border-b border-slate-700 hover:border-slate-500 pb-1"
          >
            Enter the Performance System
          </Link>
          <span className="text-slate-700">|</span>
          <a 
            href="#system"
            className="text-sm font-mono tracking-[0.1em] text-slate-600 hover:text-slate-400 transition-colors"
          >
            View a Live System Example
          </a>
        </motion.div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-slate-800/50 to-transparent" />
    </section>
  );
};
